import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useComposedRefs } from '../../hooks/use-composed-refs';
import { useOnKeyDown } from '../../hooks/use-on-key-down';
import { useRovingFocus } from '../../hooks/use-roving-focus/use-roving-focus';
import { useScrollBlock } from '../../hooks/useScrollBlock';
import { ClassNames } from '../../interfaces';
import { classes } from '../../utils/commonUtils';
import { Backdrop } from './backdrop/backdrop';
import { BackgroundHeader } from './background-header/background-header';
import { Button } from './button/button';
import { CloseButton } from './close-button/close-button';
import css from './motion-modal.module.scss';
import { ViewProvider } from './view/context';
import { View } from './view/view';

interface MotionModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  children: React.ReactNode;
  selector?: Element | null;
  withPresence?: boolean;
  classNames?: ClassNames<'container' | 'content' | 'backdrop'>;
  offsetTop?: number;
}

export function MotionModal({ isOpen, withPresence = true, selector, ...rest }: MotionModalProps) {
  if (!withPresence) {
    return isOpen ? <PortalledModal selector={selector} {...rest} /> : null;
  }

  return (
    <AnimatePresence exitBeforeEnter>{isOpen && <PortalledModal selector={selector} {...rest} />}</AnimatePresence>
  );
}

function PortalledModal({ selector, ...rest }: Omit<MotionModalProps, 'isOpen'>) {
  return createPortal(<Modal {...rest} />, selector || document.body);
}

function Modal({ onRequestClose, children, classNames, offsetTop = 32 }: Omit<MotionModalProps, 'isOpen'>) {
  const ref = useRef<HTMLDivElement>(null);
  const rovingRef = useRovingFocus<HTMLDivElement>();
  const keyDownRef = useOnKeyDown<HTMLDivElement>(() => onRequestClose(), 'Escape');
  const [blockScroll, allowScroll] = useScrollBlock();
  const composedRefs = useComposedRefs(ref, rovingRef, keyDownRef);

  const variants = {
    hidden: {
      y: 0,
      opacity: 0,
    },
    visible: {
      y: offsetTop,
      opacity: 1,
      transition: {
        type: 'tween',
        duration: 0.25,
        easing: 'easeIn',
      },
    },
    exit: {
      y: 0,
      opacity: 0,
      transition: {
        type: 'tween',
        duration: 0.25,
        easing: 'easeOut',
      },
    },
  };

  useEffect(() => {
    blockScroll();
    return () => allowScroll();
  }, [allowScroll, blockScroll]);

  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    // If the modal content is removed from the DOM, we want to
    // clone it and append it to the modal container so that there
    // is no flicker when the modal is animating the exit.
    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        record.removedNodes.forEach((node) => {
          if (!ref.current) {
            return;
          }

          // We only want to clone the node if the content is removed from the DOM.
          if (ref.current.children.length > 0) {
            return;
          }

          const clone = node.cloneNode(true);

          if (clone instanceof HTMLElement) {
            ref.current?.appendChild(clone);
          }
        });
      });
    });

    observer.observe(ref.current, {
      childList: true,
    });

    return () => observer.disconnect();
  }, []);

  return (
    <Backdrop
      onClick={onRequestClose}
      classNames={{
        backdrop: classNames?.backdrop,
        container: classNames?.container,
      }}
    >
      <motion.div
        ref={composedRefs}
        onClick={(e) => e.stopPropagation()}
        className={classes(css.content, classNames?.content)}
        style={{ '--translate-y': `${offsetTop}px` } as React.CSSProperties}
        variants={variants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <ViewProvider>{children}</ViewProvider>
      </motion.div>
    </Backdrop>
  );
}

MotionModal.Header = BackgroundHeader;
MotionModal.CloseButton = CloseButton;
MotionModal.Button = Button;
MotionModal.View = View;
MotionModal.DefaultView = View.DefaultView;
MotionModal.ViewButton = View.Button;
MotionModal.ViewBackButton = View.BackButton;
